// What happened after each call.
//
// The post-call pipeline was invisible. A lead that failed to reach Zoho was
// recorded faithfully — the status, the per-action outcome, the queued retry —
// and the only way to see any of it was a SQL query. A business would have
// discovered a revoked token by noticing that leads had stopped arriving, which
// is the worst possible way to find out.
//
// So the page leads with what needs attention, not with a table.
import React, { useCallback, useEffect, useState } from 'react';
import {
  PhoneIncoming, PhoneOutgoing, RefreshCw, Loader2, AlertCircle,
  CheckCircle2, Clock, XCircle, MinusCircle,
} from 'lucide-react';
import { agentCallsService, AgentCallEvent } from 'api/services/crmProviders.service';

/** How each status reads to someone who did not write the pipeline. */
const STATUS: Record<AgentCallEvent['status'], { label: string; tone: string; icon: React.ComponentType<{ className?: string }> }> = {
  PROCESSED:  { label: 'Done',            tone: 'text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-300', icon: CheckCircle2 },
  RECEIVED:   { label: 'Waiting',         tone: 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',        icon: Clock },
  PARTIAL:    { label: 'Retrying',        tone: 'text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300', icon: RefreshCw },
  FAILED:     { label: 'Needs attention', tone: 'text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300',         icon: XCircle },
  NO_ACTIONS: { label: 'Nothing to do',   tone: 'text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400',        icon: MinusCircle },
};

// How long the call lasted, as the backend measured it — deliberately NOT
// recomputed here.
//
// Until this morning cpass derived this from the span between the transcript's
// first and last word, which is not the call: it excludes the ring, the connect,
// the greeting before anyone speaks, and everything after the final word. The
// first real call through the hook recorded 73s against 104s actual. That is
// fixed at the source (cpass 9559818 takes startTime/endTime from call context),
// so rows written from now on are right.
//
// Rows written before it are not, and nothing in the response tells them apart.
// /v1/agent-calls returns durationSeconds and a one-line digest and never the
// raw payload — the payload is the full transcript of a private conversation,
// withheld on purpose — so the startTime/endTime that would settle it are not
// here to recompute from. The screen therefore cannot distinguish a 73 that
// means 73 from a 73 that means 104, and any rule it invented to try — a cutoff
// date, a correction factor — would silently rewrite the correct rows too. A
// number that is wrong in one place and quietly patched in another is worse than
// one that is consistently wrong: it is fixed in the database, where it can be
// checked, not guessed at in the renderer.
const duration = (s?: number) => {
  if (s == null) return '—';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

const when = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const CallActivity: React.FC = () => {
  const [calls, setCalls] = useState<AgentCallEvent[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await agentCallsService.list();
    if (res.success && res.data) {
      setCalls(res.data.calls || []);
      setCounts(res.data.counts || {});
      setError(null);
    } else {
      setError(res.error || 'Could not load calls');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const needsAttention = (counts.FAILED || 0) + (counts.PARTIAL || 0);

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Call activity</h1>
        <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>
      <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
        Every finished call the agent reported, and what was done with it afterwards —
        the lead written to your CRM, the call logged against the caller.
      </p>

      {error && (
        <div className="mb-4 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm
                        text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span>
        </div>
      )}

      {/* What needs a person, first. A count of successes helps nobody act. */}
      {needsAttention > 0 && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm
                        text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          <span className="font-medium">{needsAttention} call{needsAttention === 1 ? '' : 's'} did not reach your CRM.</span>{' '}
          Retrying ones will land by themselves once the integration is working — the details
          are on each row below. Nothing is lost: every caller is in Contacts regardless.
        </div>
      )}

      {!loading && calls.length === 0 && !error && (
        <div className="tl-card text-center py-10">
          <p className="text-sm text-gray-500">No calls reported yet.</p>
          <p className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
            An agent reports a call once it has been pushed to the runtime — every call,
            whether or not it has post-call tools. An agent last pushed before that
            changed still carries no hook: use Push to runtime on it, and its next
            call appears here.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {calls.map(c => {
          const s = STATUS[c.status] || STATUS.RECEIVED;
          const Icon = s.icon;
          const Dir = c.callType === 'OUT' ? PhoneOutgoing : PhoneIncoming;
          const actions = Object.entries(c.actions || {});
          const isOpen = open === c.id;
          return (
            <div key={c.id} className="tl-card !p-0 overflow-hidden">
              <button className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                      onClick={() => setOpen(isOpen ? null : c.id)}>
                <Dir className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {c.callType === 'OUT' ? c.callTo : c.callFrom}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${s.tone}`}>
                      <Icon className="w-3 h-3" />{s.label}
                    </span>
                    <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      {duration(c.durationSeconds)} · {when(c.receivedAt)}
                    </span>
                  </div>
                  {c.summary && (
                    <p className="text-[12px] mt-0.5 truncate" style={{ color: 'var(--text-muted)' }}>
                      “{c.summary}”
                    </p>
                  )}
                </div>
              </button>

              {isOpen && (
                <div className="px-4 pb-3 pt-1 border-t border-gray-100 dark:border-gray-700">
                  {actions.length === 0 ? (
                    <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                      {c.status === 'NO_ACTIONS'
                        ? 'This agent has no post-call tools switched on, so there was nothing to do. The call is still recorded.'
                        : 'Not processed yet.'}
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {actions.map(([tool, outcome]) => {
                        const bad = /failed|queued|could not/i.test(outcome);
                        return (
                          <li key={tool} className="text-[12px] flex gap-2">
                            <span className="font-medium text-gray-700 dark:text-gray-200 w-28 shrink-0">{tool}</span>
                            <span className={bad ? 'text-amber-700 dark:text-amber-300' : ''}
                                  style={bad ? undefined : { color: 'var(--text-muted)' }}>
                              {outcome}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {c.lastError && (
                    <p className="text-[11px] mt-2 text-red-600 dark:text-red-400">{c.lastError}</p>
                  )}
                  <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
                    agent {c.agentId} · call {c.callSid || '—'}
                    {c.attempts > 1 ? ` · ${c.attempts} attempts` : ''}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CallActivity;
